import React, { useState, useContext } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Paper,
  Button,
  Drawer,
  Radio,
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Avatar,
  IconButton,
  Tooltip,
  LinearProgress,
  Stack,
  ThemeProvider,
  useTheme,
  RadioGroup,
  FormControlLabel,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
} from "@mui/material";
import { styled, alpha } from "@mui/material/styles";
import {
  ChatBubbleOutlineOutlined as ChatBubbleOutlineOutlinedIcon,
  ArrowCircleDown as ArrowCircleDownIcon,
  ArrowCircleUp as ArrowCircleUpIcon,
  AccountBalance as AccountBalanceIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  FilterList as FilterListIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  AccountBalanceWallet as WalletIcon,
} from "@mui/icons-material";
import { pink, yellow } from "@mui/material/colors";
import { motion, AnimatePresence } from "framer-motion";
import Layout from "../components/layout/Layout.jsx";
import { useSelector } from "react-redux";

import InputsContext from "../context/InputContext";
import RecordAdd from "./RecordAdd";

import theme from "../theme/theme.js";

const bankdata = [
  {
    id: 1,
    date: new Date("2024-01-15"),
    vendor: "AMAZON PAYMENT",
    description: "Online Purchase - Electronics",
    account: "SAVINGS",
    amount: -2500.0,
    balance: 47500.0,
    in_out: "debit",
    category: "Shopping",
  },
  {
    id: 2,
    date: new Date("2024-01-14"),
    vendor: "SALARY CREDIT",
    description: "Monthly Salary - January 2024",
    account: "SAVINGS",
    amount: 50000.0,
    balance: 50000.0,
    in_out: "credit",
    category: "Income",
  },
  {
    id: 3,
    date: new Date("2024-01-13"),
    vendor: "GROCERY STORE",
    description: "Weekly Groceries",
    account: "SAVINGS",
    amount: -1200.0,
    balance: 48700.0,
    in_out: "debit",
    category: "Food",
  },
  {
    id: 4,
    date: new Date("2024-01-12"),
    vendor: "ELECTRIC BILL",
    description: "Monthly Electricity Payment",
    account: "CURRENT",
    amount: -800.0,
    balance: 49900.0,
    in_out: "debit",
    category: "Utilities",
  },
  {
    id: 5,
    date: new Date("2024-02-01"),
    vendor: "RENT PAYMENT",
    description: "February Rent",
    account: "SAVINGS",
    amount: -15000.0,
    balance: 34900.0,
    in_out: "debit",
    category: "Housing",
  },
  {
    id: 6,
    date: new Date("2024-02-05"),
    vendor: "FREELANCE INCOME",
    description: "Project Payment",
    account: "CHECKING",
    amount: 10000.0,
    balance: 60000.0,
    in_out: "credit",
    category: "Income",
  },
  {
    id: 7,
    date: new Date("2024-03-10"),
    vendor: "COFFEE SHOP",
    description: "Morning coffee",
    account: "SAVINGS",
    amount: -250.0,
    balance: 34650.0,
    in_out: "debit",
    category: "Food",
  },
  {
    id: 8,
    date: new Date("2024-03-08"),
    vendor: "SOFTWARE SUBSCRIPTION",
    description: "Monthly software fee",
    account: "CURRENT",
    amount: -500.0,
    balance: 49400.0,
    in_out: "debit",
    category: "Software",
  },
];

const mockCompanyInfo = {
  legalName: "TechCorp Solutions Pvt Ltd",
};

const mockAddress = {
  operatingOffice: {
    street: "123 Business Park, Tech City",
  },
};

const mockBankingInfo = {
  accountNumber: "XXXX-XXXX-1234",
  swiftCode: "HDFC0001234",
};

const StyledCard = styled(Card)(({ theme }) => ({
  background: `linear-gradient(135deg, ${alpha(
    theme.palette.primary.main,
    0.1
  )} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
  backdropFilter: "blur(10px)",
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  borderRadius: 16,
  boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.1)}`,
  transition: "all 0.3s ease-in-out",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: `0 12px 40px ${alpha(theme.palette.common.black, 0.15)}`,
  },
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: 16,
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.08)}`,
  "& .MuiTableHead-root": {
    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
    "& .MuiTableCell-root": {
      color: "black",
      fontWeight: 600,
      fontSize: "0.875rem",
      borderBottom: `1px solid ${alpha(theme.palette.common.white, 0.1)}`,
    },
  },
  "& .MuiTableBody-root .MuiTableRow-root": {
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: alpha(theme.palette.primary.main, 0.04),
      transform: "scale(1.001)",
    },
  },
}));

const StatCard = ({ title, value, icon, trend, color, delay }) => {
  const theme = useTheme();

  const getThemeColor = (colorPath) => {
    const paths = colorPath.split(".");
    let result = theme.palette;
    for (const path of paths) {
      if (result && typeof result === "object" && path in result) {
        result = result[path];
      } else {
        return undefined;
      }
    }
    return result;
  };

  const themeColor = getThemeColor(color);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: delay * 0.1, duration: 0.5 }}
    >
      <StyledCard>
        <CardContent>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {title}
              </Typography>
              <Typography
                variant="h4"
                fontWeight="bold"
                sx={{ color: themeColor || theme.palette.text.primary }}
              >
                {value}
              </Typography>
              {trend && (
                <Box display="flex" alignItems="center" mt={1}>
                  {trend > 0 ? (
                    <TrendingUpIcon color="success" fontSize="small" />
                  ) : (
                    <TrendingDownIcon color="error" fontSize="small" />
                  )}
                  <Typography
                    variant="caption"
                    color={trend > 0 ? "success.main" : "error.main"}
                    ml={0.5}
                  >
                    {Math.abs(trend)}%
                  </Typography>
                </Box>
              )}
            </Box>
            <Avatar
              sx={{
                bgcolor: alpha(themeColor || theme.palette.primary.main, 0.1),
                color: themeColor || theme.palette.primary.main,
                width: 56,
                height: 56,
              }}
            >
              {icon}
            </Avatar>
          </Box>
        </CardContent>
      </StyledCard>
    </motion.div>
  );
};

const Reports = () => {
  const [bankDatas, setBankDatas] = useState(bankdata);
  const [selectedValue, setSelectedValue] = useState(null);
  const [loading, setLoading] = useState(false);
  const { drawerState, toggleDrawer } = useContext(InputsContext);

  const [dateRangeDrawerOpen, setDateRangeDrawerOpen] = useState(false);
  const [vendorDrawerOpen, setVendorDrawerOpen] = useState(false);
  const [accountDrawerOpen, setAccountDrawerOpen] = useState(false);
  const [amountDrawerOpen, setAmountDrawerOpen] = useState(false);

  const [filterCriteria, setFilterCriteria] = useState({
    dateRange: "all",
    vendor: "",
    account: "",
    minAmount: "",
    maxAmount: "",
  });

  const handleChange = (event) => {
    setSelectedValue(event.target.value);
  };

  const getNestedValue = (reduxData, mockData, path, defaultValue = "N/A") => {
    let value;
    try {
      value = path.split(".").reduce((acc, part) => acc?.[part], reduxData);
    } catch (e) {
      value = undefined;
    }

    if (value === undefined || value === null || value === "") {
      try {
        value = path.split(".").reduce((acc, part) => acc?.[part], mockData);
      } catch (e) {
        value = undefined;
      }
    }
    return value !== undefined && value !== null && value !== ""
      ? value
      : defaultValue;
  };

  const handleFilterChange = (filterType, value) => {
    setFilterCriteria((prev) => ({
      ...prev,
      [filterType]: value,
    }));
  };

  const applyFilters = () => {
    setLoading(true);
    setTimeout(() => {
      let filteredData = [...bankdata];

      // 1. Apply Date Range Filter
      if (filterCriteria.dateRange && filterCriteria.dateRange !== "all") {
        const today = new Date();
        let startDate, endDate;

        switch (filterCriteria.dateRange) {
          case "lastMonth":
            startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
            endDate = new Date(today.getFullYear(), today.getMonth(), 0);
            break;
          case "lastQuarter":
            startDate = new Date(today.getFullYear(), today.getMonth() - 3, 1);
            endDate = new Date(today.getFullYear(), today.getMonth(), 0);
            break;
          default:
            break;
        }

        if (startDate && endDate) {
          filteredData = filteredData.filter((item) => {
            const itemDate = new Date(item.date);
            return itemDate >= startDate && itemDate <= endDate;
          });
        }
      }

      if (filterCriteria.vendor) {
        filteredData = filteredData.filter((item) =>
          item.vendor
            .toLowerCase()
            .includes(filterCriteria.vendor.toLowerCase())
        );
      }

      if (filterCriteria.account) {
        filteredData = filteredData.filter(
          (item) => item.account === filterCriteria.account
        );
      }

      const minAmount = parseFloat(filterCriteria.minAmount);
      const maxAmount = parseFloat(filterCriteria.maxAmount);

      if (!isNaN(minAmount) || !isNaN(maxAmount)) {
        filteredData = filteredData.filter((item) => {
          const absoluteAmount = Math.abs(item.amount);
          if (!isNaN(minAmount) && absoluteAmount < minAmount) {
            return false;
          }
          if (!isNaN(maxAmount) && absoluteAmount > maxAmount) {
            return false;
          }
          return true;
        });
      }

      setBankDatas(filteredData);
      setLoading(false);

      setDateRangeDrawerOpen(false);
      setVendorDrawerOpen(false);
      setAccountDrawerOpen(false);
      setAmountDrawerOpen(false);
    }, 500);
  };

  const selectedCompanyInfo = useSelector(
    (state) => state.kyc.selectedCompanyInfo
  );
  const selectedAddress = useSelector((state) => state.kyc.selectedAddress);
  const selectedBankingInfo = useSelector(
    (state) => state.kyc.selectedBankingInfo
  );

  const totalBalance =
    bankDatas.length > 0
      ? bankDatas.reduce((sum, item) => sum + item.balance, 0) /
        bankDatas.length
      : 0;
  const totalIncome = bankDatas
    .filter((item) => item.in_out === "credit")
    .reduce((sum, item) => sum + item.amount, 0);
  const totalExpense = Math.abs(
    bankDatas
      .filter((item) => item.in_out === "debit")
      .reduce((sum, item) => sum + item.amount, 0)
  );

  const uniqueVendors = [...new Set(bankdata.map((item) => item.vendor))];
  const uniqueAccounts = [...new Set(bankdata.map((item) => item.account))];

  return (
    <Layout>
      <ThemeProvider theme={theme}>
        <Box
          sx={{
            p: 3,
            backgroundColor: "background.default",
            minHeight: "100vh",
          }}
        >
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={4}
            >
              <Box>
                <Typography
                  variant="h4"
                  fontWeight="bold"
                  color="primary.main"
                  gutterBottom
                >
                  Bank Statement Dashboard
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Manage and analyze your financial transactions
                </Typography>
              </Box>
              <Stack direction="row" spacing={2}>
                <Tooltip title="Refresh Data">
                  <IconButton color="primary" size="large">
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Download Statement">
                  <IconButton color="primary" size="large">
                    <DownloadIcon />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Box>
          </motion.div>

          {/* Stats Cards */}
          <Grid container spacing={3} mb={4}>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Current Balance"
                value={`₹${totalBalance.toLocaleString()}`}
                icon={<WalletIcon />}
                color="primary.main"
                trend={5.2}
                delay={0}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Total Income"
                value={`₹${totalIncome.toLocaleString()}`}
                icon={<ArrowCircleDownIcon />}
                color="success.main"
                trend={12.5}
                delay={1}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Total Expenses"
                value={`₹${totalExpense.toLocaleString()}`}
                icon={<ArrowCircleUpIcon />}
                color="error.main"
                trend={-3.2}
                delay={2}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Transactions"
                value={bankDatas.length}
                icon={<AccountBalanceIcon />}
                color="info.main"
                trend={8.1}
                delay={3}
              />
            </Grid>
          </Grid>

          {/* Account Information Card */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <StyledCard sx={{ mb: 4 }}>
              <CardContent>
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  color="primary.main"
                  gutterBottom
                >
                  Account Information
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Stack spacing={2}>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">
                          Name:
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {getNestedValue(
                            selectedCompanyInfo,
                            mockCompanyInfo,
                            "legalName"
                          )}
                        </Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">
                          Customer ID:
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          CUST001234
                        </Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">
                          IFSC Code:
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {getNestedValue(
                            selectedBankingInfo,
                            mockBankingInfo,
                            "swiftCode"
                          )}
                        </Typography>
                      </Box>
                    </Stack>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Stack spacing={2}>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">
                          Address:
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {getNestedValue(
                            selectedAddress,
                            mockAddress,
                            "operatingOffice.street"
                          )}
                        </Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">
                          A/C No:
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {getNestedValue(
                            selectedBankingInfo,
                            mockBankingInfo,
                            "accountNumber"
                          )}
                        </Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">
                          A/C Type:
                        </Typography>
                        <Chip
                          label="Savings Account"
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </Box>
                    </Stack>
                  </Grid>
                </Grid>
              </CardContent>
            </StyledCard>
          </motion.div>

          {/* Filters */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <StyledCard sx={{ mb: 4 }}>
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <FilterListIcon color="primary" sx={{ mr: 1 }} />
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    color="primary.main"
                  >
                    Filters & Actions
                  </Typography>
                </Box>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={6} md={2}>
                    <Button
                      variant="outlined"
                      startIcon={<VisibilityIcon />}
                      fullWidth
                      sx={{ borderRadius: 2 }}
                    >
                      Bulk Actions
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <Button
                      variant="outlined"
                      fullWidth
                      sx={{ borderRadius: 2 }}
                      onClick={() => setDateRangeDrawerOpen(true)}
                    >
                      Date Range
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <Button
                      variant="outlined"
                      fullWidth
                      sx={{ borderRadius: 2 }}
                      onClick={() => setVendorDrawerOpen(true)}
                    >
                      Vendor
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <Button
                      variant="outlined"
                      fullWidth
                      sx={{ borderRadius: 2 }}
                      onClick={() => setAccountDrawerOpen(true)}
                    >
                      Account
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <Button
                      variant="outlined"
                      fullWidth
                      sx={{ borderRadius: 2 }}
                      onClick={() => setAmountDrawerOpen(true)}
                    >
                      Amount
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <Button
                      variant="contained"
                      fullWidth
                      sx={{ borderRadius: 2 }}
                      onClick={applyFilters}
                    >
                      Apply All
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </StyledCard>
          </motion.div>

          {/* Loading Progress */}
          <AnimatePresence>
            {loading && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <LinearProgress sx={{ mb: 2 }} />
              </motion.div>
            )}
          </AnimatePresence>

          {/* Transactions Table */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <StyledTableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Sr No.</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Vendor</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell align="right">Balance</TableCell>
                    <TableCell align="center">Type</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <AnimatePresence>
                    {bankDatas.length > 0 ? (
                      bankDatas.map((bankinfo, index) => (
                        <motion.tr
                          key={bankinfo.id}
                          component={TableRow}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: 20 }}
                          transition={{ delay: index * 0.05 }}
                          style={{ display: "table-row" }}
                          onClick={() => toggleDrawer("right", true)}
                        >
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {bankinfo.date.toLocaleDateString()}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {bankinfo.vendor}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {bankinfo.description}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={bankinfo.category}
                              size="small"
                              color={
                                bankinfo.in_out === "credit"
                                  ? "success"
                                  : "error"
                              }
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              variant="body2"
                              fontWeight="bold"
                              color={
                                bankinfo.in_out === "credit"
                                  ? "success.main"
                                  : "error.main"
                              }
                            >
                              {bankinfo.in_out === "credit" ? "+" : ""}₹
                              {Math.abs(bankinfo.amount).toLocaleString()}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight="medium">
                              ₹{bankinfo.balance.toLocaleString()}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            {bankinfo.in_out === "credit" ? (
                              <ArrowCircleDownIcon color="success" />
                            ) : (
                              <ArrowCircleUpIcon color="error" />
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <Stack
                              direction="row"
                              spacing={1}
                              justifyContent="center"
                            >
                              <Button
                                size="small"
                                variant="outlined"
                                sx={{ borderRadius: 2 }}
                                onClick={toggleDrawer("right", true)}
                              >
                                Map
                              </Button>
                              <IconButton size="small">
                                <ChatBubbleOutlineOutlinedIcon fontSize="small" />
                              </IconButton>
                              <Radio
                                onChange={handleChange}
                                size="small"
                                value={`pink-${index}`}
                                checked={selectedValue === `pink-${index}`}
                                sx={{
                                  color: pink[800],
                                  "&.Mui-checked": { color: pink[600] },
                                }}
                              />
                              <Radio
                                onChange={handleChange}
                                size="small"
                                value={`yellow-${index}`}
                                checked={selectedValue === `yellow-${index}`}
                                sx={{
                                  color: yellow[800],
                                  "&.Mui-checked": { color: yellow[600] },
                                }}
                              />
                              <Radio
                                color="success"
                                size="small"
                                onChange={handleChange}
                                value={`success-${index}`}
                                checked={selectedValue === `success-${index}`}
                              />
                            </Stack>
                          </TableCell>
                        </motion.tr>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={9} align="center">
                          <Typography variant="body1" color="text.secondary">
                            No transactions found for the selected filters.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </AnimatePresence>
                </TableBody>
              </Table>
            </StyledTableContainer>
          </motion.div>

          {/* Drawer for Record Add (Existing) */}
          <Drawer
            anchor="right"
            open={drawerState.right}
            onClose={toggleDrawer("right", false)}
            sx={{
              "& .MuiDrawer-paper": {
                borderRadius: "16px 0 0 16px",
                boxShadow: theme.shadows[7],
                borderLeft: `2px solid ${theme.palette.error.main}`,
              },
            }}
          >
            <RecordAdd />
          </Drawer>

          {/* Drawer for Date Range Filter */}
          <Drawer
            anchor="right"
            open={dateRangeDrawerOpen}
            onClose={() => setDateRangeDrawerOpen(false)}
            sx={{
              "& .MuiDrawer-paper": {
                borderRadius: "16px 0 0 16px",
                boxShadow: theme.shadows[7],
                borderLeft: `2px solid ${theme.palette.error.main}`,
              },
            }}
          >
            <Box sx={{ width: 300, p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Filter by Date Range
              </Typography>
              <RadioGroup
                aria-label="date-range"
                name="date-range-group"
                value={filterCriteria.dateRange}
                onChange={(e) =>
                  handleFilterChange("dateRange", e.target.value)
                }
              >
                <FormControlLabel
                  value="all"
                  control={<Radio />}
                  label="All Time"
                />
                <FormControlLabel
                  value="lastMonth"
                  control={<Radio />}
                  label="Last Month"
                />
                <FormControlLabel
                  value="lastQuarter"
                  control={<Radio />}
                  label="Last Quarter"
                />
              </RadioGroup>
              <Button
                variant="contained"
                onClick={applyFilters}
                sx={{ mt: 3 }}
                fullWidth
              >
                Apply Date Filter
              </Button>
            </Box>
          </Drawer>

          {/* Drawer for Vendor Filter */}
          <Drawer
            anchor="right"
            open={vendorDrawerOpen}
            onClose={() => setVendorDrawerOpen(false)}
            sx={{
              "& .MuiDrawer-paper": {
                borderRadius: "16px 0 0 16px",
                boxShadow: theme.shadows[7],
                borderLeft: `2px solid ${theme.palette.error.main}`,
              },
            }}
          >
            <Box sx={{ width: 300, p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Filter by Vendor
              </Typography>
              <TextField
                fullWidth
                label="Vendor Name"
                variant="outlined"
                value={filterCriteria.vendor}
                onChange={(e) => handleFilterChange("vendor", e.target.value)}
                sx={{ mb: 2 }}
              />
              <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
                <InputLabel>Select Vendor</InputLabel>
                <Select
                  value={filterCriteria.vendor}
                  onChange={(e) => handleFilterChange("vendor", e.target.value)}
                  label="Select Vendor"
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {uniqueVendors.map((vendor) => (
                    <MenuItem key={vendor} value={vendor}>
                      {vendor}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="contained"
                onClick={applyFilters}
                sx={{ mt: 3 }}
                fullWidth
              >
                Apply Vendor Filter
              </Button>
            </Box>
          </Drawer>

          <Drawer
            anchor="right"
            open={accountDrawerOpen}
            onClose={() => setAccountDrawerOpen(false)}
            sx={{
              "& .MuiDrawer-paper": {
                borderRadius: "16px 0 0 16px",
                boxShadow: theme.shadows[7],
                borderLeft: `2px solid ${theme.palette.error.main}`,
              },
            }}
          >
            <Box sx={{ width: 300, p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Filter by Account
              </Typography>
              <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
                <InputLabel>Select Account</InputLabel>
                <Select
                  value={filterCriteria.account}
                  onChange={(e) =>
                    handleFilterChange("account", e.target.value)
                  }
                  label="Select Account"
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {uniqueAccounts.map((account) => (
                    <MenuItem key={account} value={account}>
                      {account}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="contained"
                onClick={applyFilters}
                sx={{ mt: 3 }}
                fullWidth
              >
                Apply Account Filter
              </Button>
            </Box>
          </Drawer>

          <Drawer
            anchor="right"
            open={amountDrawerOpen}
            onClose={() => setAmountDrawerOpen(false)}
            sx={{
              "& .MuiDrawer-paper": {
                borderRadius: "16px 0 0 16px",
                boxShadow: theme.shadows[7],
                borderLeft: `2px solid ${theme.palette.error.main}`, 
              },
            }}
          >
            <Box sx={{ width: 300, p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Filter by Amount
              </Typography>
              <TextField
                fullWidth
                label="Minimum Amount"
                variant="outlined"
                type="number"
                value={filterCriteria.minAmount}
                onChange={(e) =>
                  handleFilterChange("minAmount", e.target.value)
                }
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Maximum Amount"
                variant="outlined"
                type="number"
                value={filterCriteria.maxAmount}
                onChange={(e) =>
                  handleFilterChange("maxAmount", e.target.value)
                }
                sx={{ mb: 2 }}
              />
              <Button
                variant="contained"
                onClick={applyFilters}
                sx={{ mt: 3 }}
                fullWidth
              >
                Apply Amount Filter
              </Button>
            </Box>
          </Drawer>
        </Box>
      </ThemeProvider>
    </Layout>
  );
};

export default Reports;
